import {ClientSideRowModelModule, GridOptions, RowAutoHeightModule, SortChangedEvent, themeQuartz} from 'ag-grid-community';
import {SortState} from './types';

// Modules every management table needs: client-side rows + row auto-height (the tables use
// defaultColDef.autoHeight). Registering RowAutoHeightModule here makes each table self-sufficient
// rather than relying on another screen having registered it globally first (ag-grid error #200).
export const MANAGEMENT_GRID_MODULES = [ClientSideRowModelModule, RowAutoHeightModule];

/**
 * Shared ag-grid look & behaviour for the three management tables (accounts/users/brands). Keeping
 * this in one place means the tables only declare their own colDefs.
 */
export const MANAGEMENT_GRID_THEME = themeQuartz.withParams({
  spacing: 10,
  headerBackgroundColor: 'rgba(217, 217, 217, 0.3)',
  rowHoverColor: 'rgba(233, 244, 245, 0.85)',
  fontSize: '14px',
  headerFontSize: '16px',
  fontFamily: 'Poppins',
  headerFontFamily: 'Poppins',
  headerFontWeight: 600,
  headerTextColor: '#717172;',
  cellTextColor: '#717172;',
  headerHeight: 64,
});

// A factory (not a shared const) so each grid instance gets its own options object — ag-grid is free
// to read/attach to it without cross-contaminating sibling tables.
export function createManagementGridOptions(): GridOptions {
  return {
    suppressMovableColumns: true,
    // The management screen renders its own "No <tab> found" overlay over the table region, so
    // ag-grid's built-in "No Rows To Show" would be a second empty message on the same area.
    suppressNoRowsOverlay: true,
    domLayout: 'autoHeight',
    // No rowHeight here on purpose: setting it (even to undefined) makes ag-grid run its row-height
    // resolution at init, which reads the theme's --ag-row-height CSS var before the injected theme
    // stylesheet has applied (Theming-API warning #9). Omitting it lets the theme own row height.
    defaultColDef: {
      resizable: false,
      autoHeight: true,
      sortable: true,
      wrapText: true,
      flex: 1,
    },
  };
}

/**
 * ag-grid fires sortChanged for non-user reasons too (initialization, row-data updates, flex/resize).
 * Returns a SortState only for a real header click, so callers can ignore the rest and avoid reloading
 * the table for nothing.
 */
export function toSortState(event: SortChangedEvent): SortState | null {
  if (event.source !== 'uiColumnSorted') {
    return null;
  }
  const sortModel = event.columns?.[0];
  return {
    sortBy: sortModel?.getColId() || '',
    sortDir: (sortModel?.getSort() as 'asc' | 'desc') || 'desc',
  };
}
